import { useEffect, useMemo } from "react";
import type { City } from "@/apis/city/city-apis";
import { buildWhitelabelFilter } from "@/utils/filters";
import { reformatFilter } from "@/utils/utils";
import { useDebounce, useFilterState, usePagination } from "@/v2/hooks";
import type {
  UseFilterStateOptions,
  UseFilterStateReturn,
} from "@/v2/hooks/useFilterState";
import type {
  UsePaginationOptions,
  UsePaginationReturn,
} from "@/v2/hooks/usePagination";
import {
  useCitiesList,
  useSavedCity,
  useSavedPartnerId,
  useSavedWhitelabel,
} from "@/v2/stores/cityStore";

interface UseContainerSetupOptions {
  searchValue: string;
  filterConfig?: UseFilterStateOptions;
  paginationConfig?: UsePaginationOptions;
}

interface ContainerQueryParams {
  page: number;
  search: string;
  sort: string;
  filter: string;
  size: number;
}

interface UseContainerSetupReturn
  extends UsePaginationReturn,
    UseFilterStateReturn {
  debouncedSearchTerm: string;
  savedCity: string | null;
  savedWhitelabel: number | null;
  citiesList: City[];
  queryParams: ContainerQueryParams;
}

/**
 * Consolidates common container setup logic including:
 * - Debounced search
 * - Pagination state
 * - Filter state
 * - Redux city/whitelabel selectors
 * - Query params construction
 *
 * @example
 * const [searchValue, setSearchValue] = useState('')
 *
 * const {
 *   debouncedSearchTerm,
 *   currentPage,
 *   pageSize,
 *   handlePageChange,
 *   handlePageSizeChange,
 *   filterVisible,
 *   filterValues,
 *   appliedFilters,
 *   filterChips,
 *   activeFilterCount,
 *   changeSelectorValue,
 *   applyFilters,
 *   removeFilter,
 *   openFilter,
 *   closeFilter,
 *   savedCity,
 *   savedWhitelabel,
 *   citiesList,
 *   queryParams,
 * } = useContainerSetup({
 *   searchValue,
 *   paginationConfig: { initialPageSize: 10 },
 * })
 *
 * const { data } = useQuery(queryParams)
 */
export function useContainerSetup({
  searchValue,
  filterConfig = {},
  paginationConfig = {},
}: UseContainerSetupOptions): UseContainerSetupReturn {
  const debouncedSearchTerm = useDebounce(searchValue, 500);

  const pagination = usePagination(paginationConfig);
  const { zeroBasedPage, pageSize } = pagination;

  const filterState = useFilterState(filterConfig);
  const { appliedFilters } = filterState;

  const savedCity = useSavedCity();
  const savedWhitelabel = useSavedWhitelabel();
  const savedPartnerId = useSavedPartnerId();
  const citiesList = useCitiesList();

  useEffect(() => {
    pagination.resetPage();
  }, [savedCity, savedWhitelabel, savedPartnerId]);

  const queryParams = useMemo<ContainerQueryParams>(() => {
    const whitelabelFilter = buildWhitelabelFilter(savedCity, savedWhitelabel);
    const partnerFilter =
      savedPartnerId != null ? `&partnerId=${savedPartnerId}` : "";
    const filterStr =
      reformatFilter(appliedFilters) +
      (savedCity ? "&" + savedCity : "") +
      whitelabelFilter +
      partnerFilter;
    return {
      page: zeroBasedPage,
      search: debouncedSearchTerm,
      sort: "",
      filter: filterStr,
      size: pageSize,
    };
  }, [
    zeroBasedPage,
    debouncedSearchTerm,
    savedCity,
    savedWhitelabel,
    savedPartnerId,
    appliedFilters,
    pageSize,
  ]);

  return {
    debouncedSearchTerm,
    ...pagination,
    ...filterState,
    savedCity,
    savedWhitelabel,
    citiesList,
    queryParams,
  };
}

export type {
  UseContainerSetupOptions,
  ContainerQueryParams,
  UseContainerSetupReturn,
};
